import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';

const Web3Features = () => {
  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="section-container">
        {/* The smart wallet advantage Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The smart wallet advantage.
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 mb-16 max-w-4xl">
            Simplify the decentralized experience with the smart contract-based Blockfirst Capital Web3 Wallet.
          </p>
        </motion.div>
        
        {/* Feature List */}
        <div className="space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Create your Web3 identity
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Build your on-chain persona and aggregate all your crypto and NFTs under one identity.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Save on transaction fees
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Batch multiple transactions to execute them at once and save on gas fees.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Choose how you pay fees
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Cover gas fees across all supported networks with native tokens, a variety of stablecoins, or wrapped Ethereum (wETH).
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Manage your portfolio
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Connect multiple wallets under one single identity to track all your crypto and NFTs from a single dashboard.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Optimize your swap
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Aggregate liquidity from multiple decentralized exchanges (DEXs) to get the best possible price. Trade assets between connected wallets to save on time and fees.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Interact with five networks
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Send, receive, and swap crypto on Ethereum, Polygon, BNB Chain, Avalanche, and Fantom.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="flex items-start space-x-6"
          >
            <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4">
                Earn yield with DeFi
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Supply assets and earn passive income via protocols with just a couple of clicks.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Connect Wallet Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.location.href = '/web3-connect'}
            className="bg-gray-900 hover:bg-gray-800 text-white px-10 py-5 rounded-xl font-semibold inline-flex items-center gap-3 text-lg transition-colors"
          >
            Connect Wallet
            <ArrowUpRightIcon className="w-6 h-6" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Web3Features; 